import { sdk } from '@/services/sdk';
import { committed, displayNameOf, nowIso } from '@/shared/data';
import { newCode } from '@/shared/pricing';
import { CUSTOMER_SELF_FIELDS } from '@/shared/fields';

const { eq } = window.ApperSDK.CoreSDK;

/** The signed-in customer's own customers_c record, or null when they have none yet. */
export async function findMyCustomer(user) {
  const email = user?.emailAddress;
  if (!email) return null;
  const r = await sdk.table('customers_c').select(CUSTOMER_SELF_FIELDS).where(eq('email_c', email)).limit(1).fetch();
  if (!r.success) throw new Error(r.message || 'Could not load your profile');
  return r.data?.[0] ?? null;
}

/** Find the customer's record, creating it on their first order. Returns null after toasting on failure. */
export async function ensureMyCustomer(user, info = {}) {
  const existing = await findMyCustomer(user);
  if (existing) {
    const patch = {};
    if (info.optIn && !existing.marketing_opt_in_c) patch.marketing_opt_in_c = true;
    if (info.verified && !existing.email_verified_c) {
      patch.email_verified_c = true;
      patch.email_verified_at_c = nowIso();
    }
    if (info.handle && !existing.contact_handle_c) {
      patch.contact_method_c = info.contactMethod || 'Discord';
      patch.contact_handle_c = info.handle;
    }
    if (Object.keys(patch).length) {
      await sdk.table('customers_c').update({ Id: existing.Id, ...patch });
    }
    return existing;
  }

  const res = await sdk.table('customers_c').create({
    Name: info.name || displayNameOf(user),
    email_c: user?.emailAddress || info.email,
    contact_method_c: info.contactMethod || 'Discord',
    contact_handle_c: info.handle || '',
    referral_code_c: newCode('REF-', 6),
    marketing_opt_in_c: !!info.optIn,
    expiry_reminders_c: true,
    email_verified_c: !!info.verified,
    ...(info.verified ? { email_verified_at_c: nowIso() } : {}),
  });
  return committed(res);
}

/** Flag the signed-in customer's record as email-verified. Quiet: the session already holds the truth. */
export async function markMyEmailVerified(user) {
  try {
    const existing = await findMyCustomer(user);
    if (!existing || existing.email_verified_c) return existing;
    await sdk.table('customers_c').update({ Id: existing.Id, email_verified_c: true, email_verified_at_c: nowIso() });
    return { ...existing, email_verified_c: true };
  } catch {
    return null;
  }
}

/** Orders the signed-in customer can see (row security scopes them). */
export async function myOrders(fields = ['Name', 'plan_c', 'term_c', 'connections_c', 'status_c']) {
  const r = await sdk.table('orders_c').select(fields).orderByDesc('CreatedOn').limit(100).fetch();
  if (!r.success) throw new Error(r.message || 'Could not load your orders');
  return r.data ?? [];
}
